<script setup>
import { ref, computed } from 'vue'
import { Bars3Icon, ArrowLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import { DialogClose, DialogContent, DialogPortal, DialogRoot, DialogTitle, DialogTrigger } from 'reka-ui'
import Button from '@/Components/Buttons/Button.vue'
import { ChevronLeftIcon } from 'lucide-vue-next'
import SiteNavigationListItem from '@/Layouts/Navigation//SiteNavigationListItem.vue'

const props = defineProps({
  navItems: {
    type: Array,
    default: () => [],
  },
})

const navigation = {
  items: props.navItems.map((item) => ({
    label: item.title,
    href: item.permalink || item.url,
    items: item.children?.flatMap(
      (child) =>
        child.children?.map((grandchild) => ({
          label: grandchild.title,
          href: grandchild.permalink || grandchild.url,
        })) || [],
    ),
  })),
}

const currentLevel = ref(0)
const navigationStack = ref([{ menu: navigation, previousTitle: null }])
const lastClickedTitle = ref(null)

const navigateForward = (item) => {
  if (item.items) {
    lastClickedTitle.value = item.label
    navigationStack.value.push({ menu: item, previousTitle: item.label })
    currentLevel.value++
  } else if (item.href) {
    window.location.href = item.href
  }
}

const navigateBack = () => {
  if (currentLevel.value > 0) {
    navigationStack.value.pop()
    currentLevel.value--
    lastClickedTitle.value = navigationStack.value[navigationStack.value.length - 1]?.previousTitle || 'Back'
  }
}

const getCurrentMenu = computed(() => {
  return navigationStack.value[navigationStack.value.length - 1].menu
})

const getPreviousMenuTitle = computed(() => {
  return lastClickedTitle.value || 'Back'
})
</script>

<template>
  <DialogRoot>
    <DialogTrigger>
      <Button
        class="flex items-center lg:hidden py-4"
        size="sm"
        variant="primary"
      >
        <span class="pr-4">Menu</span>
        <Bars3Icon
          class="h-7 w-7"
          aria-hidden="true"
        />
      </Button>
    </DialogTrigger>
    <DialogPortal>
      <DialogContent
        class="fixed inset-0 p-5 bg-indigo min-w-[280px] z-40 animate-slideIn data-[state=closed]:animate-slideOut"
      >
        <header class="flex justify-between mb-12">
          <div class="flex relative h-16">
            <img
              src="/images/logo.png"
              srcset="/images/logo@2x.png 2x"
              alt="Suicide Prevention Scotland logo"
            />
          </div>
          <DialogClose aria-label="Close">
            <Button
              variant=""
              class="rounded-none flex items-center bg-white text-indigo hover:bg-gray-300 h-full"
              size="sm"
            >
              Menu
              <XMarkIcon class="h-8 w-8 fill-indigo" />
            </Button>
          </DialogClose>
        </header>

        <nav class="space-y-1">
          <template v-if="currentLevel > 0">
            <button
              @click="navigateBack"
              class="w-full flex items-center hover:bg-white focus:bg-yellow focus:text-indigo text-white hover:text-indigo py-2 px-3 border-b border-white"
            >
              <ChevronLeftIcon class="h-5 w-5 mr-4" />
              {{ getPreviousMenuTitle }}
            </button>
          </template>

          <template
            v-for="(item, index) in getCurrentMenu.items"
            :key="item.label"
          >
            <button
              v-if="item.items?.length > 0"
              @click="navigateForward(item)"
              :class="`w-full flex items-center justify-between hover:bg-white focus:bg-yellow focus:text-indigo text-white hover:text-indigo  ${item.items ? 'py-2 px-3 border-b border-white' : ' underline py-2 px-3'}`"
            >
              <span>{{ item.label }}</span>
              <ChevronRightIcon :class="`h-5 w-5 ${!item.items ? 'hidden' : ''}`" />
            </button>
            <a
              v-else
              :href="item.href"
              role="button"
              :aria-label="`Link to {{item.href}}`"
              class="w-full flex items-center justify-between hover:bg-white focus:bg-yellow focus:text-indigo text-white hover:text-indigo underline py-2 px-3"
            >
              <span>{{ item.label }}</span>
            </a>
            <hr
              v-if="!item.items && index === getCurrentMenu.items.length - 1"
              class="border-white"
            />
          </template>
        </nav>
        <div class="mt-8">
          <h3 class="text-white font-sans-pt font-bold text-lg leading-8 mb-4">Services available 24 hours</h3>
          <ul class="grid gap-y-[10px]">
            <SiteNavigationListItem href="tel:999">
              <span class="font-bold">999</span>
              (Emergency)
            </SiteNavigationListItem>
            <SiteNavigationListItem href="tel:116123">
              <span class="font-bold">116 123</span>
              Samaritans
            </SiteNavigationListItem>
            <SiteNavigationListItem href="sms:85258?body=SHOUT">
              Text
              <b>'SHOUT'</b>
              to
              <span class="font-bold">85258</span>
            </SiteNavigationListItem>
            <SiteNavigationListItem href="tel:111">
              <span class="font-bold">111</span>
              NHS24
            </SiteNavigationListItem>
            <SiteNavigationListItem href="tel:0800 1111">
              <span class="font-bold">0800 1111</span>
              Childline
            </SiteNavigationListItem>
            <SiteNavigationListItem href="tel:0800 068 4141">
              <p>
                <span class="font-bold">0800 068 4141</span>
                <span>HOPELINE247</span>
              </p>
            </SiteNavigationListItem>
          </ul>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
